<template>
  <Content
    class="ship-order-consolidation"
    header-icon="map-marked-alt"
    :header-label="headerText"
    :loading="isAnyLoading"
    :class="{ blocked: !selectedClient }"
    aside-position="left"
    full-width
    remove-aside-card-wrapper
    remove-card-wrapper
  >
    <template #aside>
      <OpenShipOrdersSection
        id="open-ship-orders"
        :key="selectedClient?.naturalId"
        v-model="criteria"
        :consolidation-candidates="availableConsolidationCandidates"
        :search-results="searchResults"
        @add-ship-order="onAddShipOrder"
        @page="onPage"
        @sort="onSort"
        @refresh-candidates="runSearchDebounced"
      />
    </template>

    <ConsolidatedShipmentSection
      id="consolidated-shipment"
      v-model="proposedShipment"
      :ship-orders="consolidationCandidates"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template
        v-if="proposedShipment.shipOrders.length && !!selectedClient"
        #message
      >
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('shipments.shipments'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>

    <div class="overlay">&nbsp;</div>
  </Content>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import { usePrimeVue } from 'primevue/config';
import type { Ref } from 'vue';
import {
  computed,
  h,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  useQueryParamsForSearch,
  useToast,
  useUid,
} from '@rygen/rygen-ui/composables';
import { lowerCase } from '@rygen/rygen-ui/i18n';
import {
  DomainReference,
  DomainTree,
  DomainType,
  Loadable,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { Order, Shipment, ShipOrder, ShipOrderSearchResult } from '@/model';
import {
  ConsolidatedShipment,
  ShipOrderSearchCriteria,
  ValidShipOrder,
} from '@/model/consolidation';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ConsolidatedShipmentSection from '@/components/shipmentPlanning/consolidation/ConsolidatedShipmentSection.vue';
import OpenShipOrdersSection from '@/components/shipmentPlanning/consolidation/OpenShipOrdersSection.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import Message from 'primevue/message';
import { PageState } from 'primevue/paginator';

const OWNING_CLIENT_MESSAGE_KEY =
  'ship-order-consolidation-owning-client-message';

const props = defineProps({
  shipmentId: {
    type: Number,
    default: () => undefined,
  },
  orderId: {
    type: Number,
    default: () => undefined,
  },
});
const { shipmentId, orderId } = toRefs(props);

const { clientTreeMap, clients, loadClients } = useClientStore();
const { handleRequiredDomainRedirect, selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, owningClient, setParentEntity } =
  useParentEntityStore();
const { criteria, runSearchDebounced } = useQueryParamsForSearch({
  factoryMethod: (opts?: Partial<ShipOrderSearchCriteria>) =>
    new ShipOrderSearchCriteria(opts),
  routeName: 'shipOrderConsolidation',
  runSearch: async (
    criteria: ShipOrderSearchCriteria,
    cancelRequest?: CancelTokenSource,
  ) => {
    if (!selectedClient.value) {
      return;
    }

    try {
      searchResults.value.loading = true;
      searchResults.value.data = await api.shipmentPlanningConsolidation.search(
        criteria,
        cancelRequest,
      );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      searchResults.value.error = e;
      console.error('Error loading consolidation candidates', e);
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('consolidation.candidates', 2),
        }),
        error: e,
      });
    } finally {
      searchResults.value.loading = false;
    }
  },
});
const router = useRouter();
const toast = useToast();
const { uid } = useUid('ship-order-consolidation');
provide('scope', uid);

const proposedShipment: Ref<ConsolidatedShipment> = ref(
  new ConsolidatedShipment(),
) as Ref<ConsolidatedShipment>;

const searchResults: Ref<Loadable<ShipOrderSearchResult>> = ref({
  loading: false,
});

const selectedClient: Ref<DomainTree | undefined> = ref();
const isChangeDomainModalVisible = ref(false);

/** The `Order`/`Shipment` specified by id in query params */
const targetEntity: Ref<Loadable<Order | Shipment>> = ref({ loading: false });

/** `ShipOrder`s for `targetEntity` */
const shipOrders: Ref<Loadable<ShipOrder[]>> = ref({ loading: false });

const isAnyLoading = computed(() => {
  return (
    searchResults.value.loading ||
    targetEntity.value.loading ||
    shipOrders.value.loading
  );
});

const headerText = computed(() => {
  return proposedShipment.value.isExistingShipment
    ? t('common.updateParam', { param: t('shipments.shipments') })
    : t('common.newParam', { param: t('shipments.shipments') });
});

const consolidationCandidates: Ref<ValidShipOrder[]> = computed(() => {
  return (
    searchResults.value.data?.content
      ?.map((so) => {
        try {
          return new ValidShipOrder(so);
        } catch (e) {
          console.error(e);
          return null;
        }
      })
      ?.filter(notNull) ?? []
  );
});

const availableConsolidationCandidates: Ref<Loadable<ShipOrder[]>> = computed(
  () => {
    return {
      loading: searchResults.value.loading,
      data: consolidationCandidates.value.filter(
        (so) => !proposedShipment.value.shipOrderIds.includes(so.id),
      ),
    };
  },
);

function onAddShipOrder(shipOrder: ShipOrder) {
  proposedShipment.value.addShipOrder(shipOrder);
}

const onPage = (event: PageState) => {
  criteria.value.pageSize = event.rows;
  criteria.value.page = event.page;
};

const onSort = (event: Record<string, unknown>) => {
  criteria.value.sortField = event.sortField as keyof ShipOrder;
  criteria.value.sortOrder = event.sortOrder as -1 | 0 | 1;
};

const loadOrder = async (orderId?: number) => {
  targetEntity.value.data = undefined;
  if (!orderId) {
    return;
  }

  try {
    targetEntity.value.loading = true;
    targetEntity.value.data = await api.order.findById(orderId);
    selectedClient.value = targetEntity.value.data.owner?.id
      ? clientTreeMap.value.get(targetEntity.value.data.owner.id)
      : undefined;
  } catch (e) {
    if (axios.isCancel(e) || (await handleRequiredDomainRedirect(e))) {
      return false;
    }

    targetEntity.value.error = e as Error;
    console.error('Error loading order', e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('orders.orders'),
        id: orderId,
      }),
      error: e,
    });
  } finally {
    targetEntity.value.loading = false;
  }
};

const loadShipment = async (shipmentId?: number) => {
  targetEntity.value.data = undefined;
  if (!shipmentId) {
    return;
  }

  try {
    targetEntity.value.loading = true;
    targetEntity.value.data = await api.shipment.findById(shipmentId);
    selectedClient.value = targetEntity.value.data.owner?.id
      ? clientTreeMap.value.get(targetEntity.value.data.owner.id)
      : undefined;
  } catch (e) {
    if (axios.isCancel(e) || (await handleRequiredDomainRedirect(e))) {
      return false;
    }

    targetEntity.value.error = e as Error;
    console.error('Error loading shipment', e);
    toast.addError({
      detail: t('errors.loadingParamWithId', {
        param: t('shipments.shipments'),
        id: shipmentId,
      }),
      error: e,
    });
  } finally {
    targetEntity.value.loading = false;
  }
};

const loadRelatedShipOrders = async (): Promise<void> => {
  const target = targetEntity.value.data;
  if (!target?.id) {
    return;
  }

  const isOrder = Object.getPrototypeOf(target).constructor === Order;
  try {
    shipOrders.value.loading = true;
    shipOrders.value.data = isOrder
      ? await api.order.findAllRelatedShipOrders(target.id)
      : await api.shipment.findAllRelatedShipOrders(target.id);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(
      `Error loading ship orders for ${isOrder ? 'order' : 'shipment'} ${target.id}`,
      e,
    );
    shipOrders.value.data = undefined;
    shipOrders.value.error = e;
    toast.addError({
      detail: t('errors.loadingParamForParamWithId', {
        param: lowerCase(t('consolidation.candidates', 2)),
        param2: lowerCase(
          isOrder ? t('orders.orders') : t('shipments.shipments'),
        ),
        id: orderId,
      }),
      error: e,
    });
  } finally {
    shipOrders.value.loading = false;
  }
};

const updateProposedShipmentWithRelatedShipOrders = async () => {
  await loadRelatedShipOrders();
  shipOrders.value.data
    ?.filter(notNull)
    ?.forEach((shipOrder) => proposedShipment.value.addShipOrder(shipOrder));
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
};

const updateSelectedClient = (currentDomain: DomainReference | undefined) => {
  selectedClient.value =
    currentDomain?.domainType === DomainType.Client && currentDomain.id
      ? clientTreeMap.value.get(currentDomain.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(selectedDomain, (newVal: DomainReference | undefined) => {
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  (newVal: DomainTree | undefined) => {
    if (newVal) {
      proposedShipment.value = new ConsolidatedShipment({
        owner: newVal?.asDomainReference() ?? selectedDomain.value,
      });
      setParentEntity(proposedShipment);

      addMessage({
        id: OWNING_CLIENT_MESSAGE_KEY,
        severity: 'info',
        text: t('common.assignedToParam', { param: newVal.name }),
        iconSlot: () =>
          h(ClientAvatar, {
            name: newVal.name,
            logo: newVal.branding?.avatar?.signedUrl,
          }),
        onClick: () => (isChangeDomainModalVisible.value = true),
      });
    }
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

watch(owningClient, () => {
  runSearchDebounced();
});

watch(
  () => orderId?.value,
  async (newVal: number | undefined) => {
    await loadOrder(newVal);
    if (targetEntity.value.data) {
      updateProposedShipmentWithRelatedShipOrders();
    }
  },
  { immediate: true },
);

watch(
  () => shipmentId?.value,
  async (newVal: number | undefined) => {
    await loadShipment(newVal);
    if (targetEntity.value.data) {
      updateProposedShipmentWithRelatedShipOrders();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await loadClients();
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});

defineExpose({
  $primevue: usePrimeVue(),
});
</script>

<style>
.ship-order-consolidation.content {
  &.blocked {
    .content-container {
      .content-container-top-row {
        .overlay:not(.popup-inputgroup-overlay) {
          z-index: 99;
          background-color: rgb(from var(--p-surface-100) r g b / 0.65);
        }
      }
    }
  }

  .content-container {
    position: relative;

    .content-container-top-row {
      display: flex;
      gap: var(--gap-2);

      .overlay:not(.popup-inputgroup-overlay) {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: -1;
      }
    }
  }
}
</style>
